import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { toPlainText } from '@portabletext/react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ButtonVanilla,
  CloseIcon,
  Modal,
  Typography,
  useMediaQuery
} from '@ag1/ui';
import type { NbaOfferPopup as NbaOfferPopupProps, SanityKeyed } from 'types/studio/studio';
import {
  nbaOfferClickEvent,
  nbaOfferDismissEvent,
  nbaOfferViewEvent
} from 'analytics/events/studio';
import { useKetchConsent } from 'analytics/useKetchConsent';
import { useSessionValue } from 'appLayer/auth';
import { useHasPouchOrPackSubscription } from 'processes/ShopListProducts/hooks/useHasPouchOrPackSubscription';
import { useLocale } from 'shared/hooks/useLocale';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { CustomPortableText } from 'shared/studio/PortableText';
import { StudioBaseIconV2 } from 'shared/studio/StudioBaseIcon/StudioBaseIcon';
import { safeLocalStorage } from 'shared/utils/safeLocalStorage';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import type { CommonSectionProps } from '../types';
import { isButtonVanillaVariant, normalizeTimer } from './utils';
import * as styles from './NbaOfferPopup.css';

export const NbaOfferPopup = ({
  isMembersOnlyPopup,
  membersOfferType = 'NBA_15ct_offer',
  nonMembersOfferType = 'NBA_5ct_offer',
  timer = 0,
  baseImage,
  badgeText,
  badgeTextColor,
  badgeBackgroundColor,
  badgeIcon,
  headline,
  text,
  baseButtonLink
}: SanityKeyed<NbaOfferPopupProps> & CommonSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { hasActiveSubscriptions } = useHasPouchOrPackSubscription();
  const aboveLarge = useMediaQuery('l');
  const session = useSessionValue();
  const isLoggedIn = session?.customer;
  const locale = useLocale();
  const adsAllowed = useKetchConsent('ad_storage');

  // analytics
  const customerId = session?.customer?.id || null;
  const offerType = isLoggedIn && isMembersOnlyPopup ? membersOfferType : nonMembersOfferType;
  const offerHeadline = toPlainText(headline);
  const offerLink = getSanityLinkUrl(baseButtonLink?.btnLink, locale) ?? '';
  const clickText = baseButtonLink?.portableText ? toPlainText(baseButtonLink?.portableText) : '';
  const timestamp = new Date().toISOString();

  const handleClose = () => {
    setIsOpen(false);
    nbaOfferDismissEvent({ offerType, offerHeadline });
    safeLocalStorage.setItem(offerType, 'true');
  };

  const handleClick = () => {
    nbaOfferClickEvent({
      customerId,
      offerType,
      offerHeadline,
      offerLink,
      clickText,
      clickDate: timestamp
    });
    safeLocalStorage.setItem(offerType, 'true');
  };

  useEffect(() => {
    const hasDismissedOffer = safeLocalStorage.getItem(offerType);
    const normalizedTimer = normalizeTimer(timer);

    if (!hasDismissedOffer && adsAllowed) {
      const popupDelay = setTimeout(() => {
        setIsOpen(true);
      }, normalizedTimer);

      return () => clearTimeout(popupDelay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adsAllowed]);

  useEffect(() => {
    if (isOpen && adsAllowed)
      nbaOfferViewEvent({ customerId, offerType, offerHeadline, offerLink, viewDate: timestamp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, adsAllowed]);

  // ButtonVanilla props
  const variant = baseButtonLink?.variant ?? '';
  const iconPosition = baseButtonLink?.iconOptions?.position;
  let icon = null;
  if (baseButtonLink?.showIcon) {
    if (iconPosition === 'left') {
      icon = <ArrowLeftIcon size="24" />;
    } else if (iconPosition === 'right') {
      icon = <ArrowRightIcon size="24" />;
    }
  }

  if (!adsAllowed || hasActiveSubscriptions || (!isLoggedIn && isMembersOnlyPopup)) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      mode="center"
      overlayClassName={styles.overlay}
      bodyClassName={styles.body}
      className={styles.modal}>
      <div className={styles.modalInner}>
        <ButtonVanilla
          onClick={handleClose}
          className={styles.closeButton}
          variant="primary"
          size="XS"
          shape="round"
          isIconOnly
          iconPosition="right"
          icon={<CloseIcon size={20} />}
        />
        {baseImage?.image && (
          <StudioBaseImageV2
            image={baseImage.image}
            mobileImage={baseImage.mobileImage}
            tabletImage={baseImage.tabletImage}
            className={styles.image}
          />
        )}
        <div className={styles.content}>
          {badgeText && (
            <div
              className={styles.badgeWrapper}
              style={{
                ...(badgeBackgroundColor ? { backgroundColor: badgeBackgroundColor.value } : {})
              }}>
              {badgeIcon?.asset && (
                <StudioBaseIconV2 icon={badgeIcon} className={styles.badgeIcon} />
              )}
              <Typography
                variant="bodyMono"
                size="2"
                noPadding
                className={clsx(
                  styles.badgeText,
                  badgeTextColor === 'white' && styles.badgeTextColorInverse
                )}>
                {badgeText}
              </Typography>
            </div>
          )}
          <Typography variant="headlineSans" size="2" noPadding className={styles.headline}>
            <CustomPortableText value={headline} tag="h1" />
          </Typography>
          <Typography variant="body" size="1" noPadding className={styles.text}>
            <CustomPortableText value={text} />
          </Typography>
          {baseButtonLink?.portableText && (
            <ButtonVanilla
              className={styles.button}
              href={offerLink}
              onClick={handleClick}
              variant={isButtonVanillaVariant(variant) ? variant : 'primary'}
              size={aboveLarge ? 'L' : 'M'}
              shape="round"
              iconPosition={iconPosition}
              icon={icon}>
              <CustomPortableText value={baseButtonLink.portableText} />
            </ButtonVanilla>
          )}
        </div>
      </div>
    </Modal>
  );
};
